import React, { ChangeEvent, useMemo } from 'react'

import { Table, TableHead, TableBody, TableRow, TableCell, Checkbox, Box, Typography } from '@mui/material'
import { type SxProps } from '@mui/system'
import type { ColumnDef, RowSelectionState, Updater, ExpandedState, Row } from '@tanstack/react-table'
import { flexRender, getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table'

interface TanStackTableProps<T> {
  columns: ColumnDef<T>[]
  tableData: T[]
  onRowClick?: (row: T) => void
  enableRowSelection?: boolean
  enableMultiRowSelection?: boolean
  totalText?: string
  rowSelection?: Record<number, boolean>
  setRowSelection?: (rowSelection: Updater<RowSelectionState>) => void
  sx?: SxProps
  backgroundHeader?: boolean
  borderRadius?: string
  borderRight?: string
  expanded?: ExpandedState
  onExpandedChange?: (updater: Updater<ExpandedState>) => void
  renderSubComponent?: (props: { row: Row<T> }) => React.ReactElement
  getRowCanExpand?: (row: Row<T>) => boolean
}

// Convert the table component to use MUI
export function TanStackTable<T extends NonNullable<unknown>>({
  columns = [],
  tableData = [],
  onRowClick,
  enableRowSelection,
  sx = {},
  totalText,
  rowSelection,
  setRowSelection,
  borderRight,
  expanded,
  onExpandedChange,
  renderSubComponent,
  getRowCanExpand
}: TanStackTableProps<T>) {
  const { __ } = useTranslator()

  function onCheckboxChange(callback: any) {
    return function (event: ChangeEvent) {
      event.stopPropagation()
      callback()(event)
    }
  }

  const columnDefs = useMemo<ColumnDef<T>[]>(() => {
    if (!enableRowSelection) return columns || []

    return [
      {
        id: 'select',
        header: () => (
          <Checkbox
            checked={table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={onCheckboxChange(table.getToggleAllRowsSelectedHandler)}
            onClick={event => event.stopPropagation()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            indeterminate={row.getIsSomeSelected()}
            disabled={!row.getCanSelect()}
            onChange={onCheckboxChange(row.getToggleSelectedHandler)}
            onClick={event => event.stopPropagation()}
          />
        ),
        size: 50
      },
      ...columns
    ]
  }, [columns, enableRowSelection])

  const table = useReactTable({
    columns: columnDefs,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
      expanded
    },
    enableRowSelection,
    onRowSelectionChange: setRowSelection,
    getRowId: (row: any) => row.id,
    onExpandedChange,
    getRowCanExpand,
    getExpandedRowModel: getExpandedRowModel()
  })

  return (
    <Box
      sx={{
        overflowX: 'auto',
        overflowY: 'visible',
        width: '100%',
        borderTop: '1px solid #e0e0e0',
        ...sx
      }}
    >
      {totalText && (
        <Typography variant='subtitle1' sx={{ padding: 2 }}>
          {totalText}
        </Typography>
      )}
      <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell
                  key={header.id}
                  colSpan={header.colSpan}
                  style={
                    header.column.columnDef.size
                      ? {
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                          textTransform: 'uppercase'
                        }
                      : {}
                  }
                >
                  <Box sx={{ borderRight: header.id === 'select' ? 'none' : borderRight }}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {tableData.length === 0 && (
            <TableRow>
              <TableCell colSpan={table.getAllFlatColumns().length}>
                <Box display='flex' justifyContent='center' alignItems='center' height={300}>
                  <Typography className='text-[14px] font-normal text-[#2F2B3D]'>{__('No Data')}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
          {table.getRowModel().rows.map(row => (
            <React.Fragment key={row.id}>
              <TableRow
                sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                onClick={() => {
                  if (onRowClick) onRowClick(row.original)
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <TableCell
                    key={cell.id}
                    style={
                      cell.column.columnDef.size
                        ? {
                            width: cell.column.columnDef.size,
                            minWidth: cell.column.columnDef.size,
                            maxWidth: cell.column.columnDef.size,
                            padding: '10px 16px'
                          }
                        : {}
                    }
                    onClick={e => {
                      if (cell.column.columnDef.id === 'select') {
                        e.stopPropagation()
                      }
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
              {row.getIsExpanded() && renderSubComponent && (
                <TableRow key={`${row.id}-sub`}>
                  <TableCell colSpan={row.getVisibleCells().length} sx={{ padding: 0 }}>
                    {renderSubComponent({ row })}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}
